import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { isEqual, cloneDeep } from "lodash";
import { send } from "@utils/fetch";
import { captureException } from "@utils/sentry";

const SUBSCRIPTION_STATUS = {
  ACTIVE: "ACTIVE",
  PAST_DUE: "PAST_DUE",
  PENDING: "PENDING",
  INACTIVE: "INACTIVE",
  FRAUDULENT: "FRAUDULENT",
};

const SUBSCRIPTION_INITIAL_STATE = {
  subscriptions: [],
  determinations: {
    inTrial: null,
    inSubscription: null,
    autoRenewalEnabled: null,
  },
};

const subscriptionSlice = createSlice({
  name: "subscription",
  initialState: cloneDeep(SUBSCRIPTION_INITIAL_STATE),
  reducers: {
    updateSubscriptions(state, action) {
      return Object.assign({}, state, action.payload);
    },
    updateDeterminations: {
      reducer: (state, action) => {
        return Object.assign({}, state, action.payload);
      },
      prepare: (subscriptions) => {
        if (!subscriptions.length)
          return {
            payload: {
              determinations: cloneDeep(
                SUBSCRIPTION_INITIAL_STATE.determinations
              ),
            },
          };

        return {
          payload: {
            determinations: {
              inTrial: inTrial(subscriptions),
              inSubscription: inSubscription(subscriptions),
              autoRenewalEnabled: autoRenewalEnabled(subscriptions),
            },
          },
        };
      },
    },
  },
});

/**
 * Return true if any subscription is both active and in trial.
 * @param {*} subscriptions
 */
const inTrial = (subscriptions) =>
  subscriptions.some(
    (subscription) =>
      subscription.in_trial_period &&
      subscription.status === SUBSCRIPTION_STATUS.ACTIVE
  );

/**
 * Return true if any subscription is both active and not in trial or simply past due (not in trial implied).
 * @param {*} subscriptions
 */
const inSubscription = (subscriptions) =>
  subscriptions.some(
    (subscription) =>
      (subscription.status === SUBSCRIPTION_STATUS.ACTIVE &&
        !subscription.in_trial_period) ||
      subscription.status === SUBSCRIPTION_STATUS.PAST_DUE
  );

/**
 * Return true if any subscription has auto renewal enabled
 * @param {*} subscriptions
 */
const autoRenewalEnabled = (subscriptions) =>
  subscriptions.some((subscription) => subscription.auto_renewal_enabled);

/**
 * Fetches subscriptions by user id and updates the slice (subscriptions and determinations).
 * @param {*} user_id
 */
export const fetchSubscriptionsByUserId = createAsyncThunk(
  "subscription/fetchSubscriptionsByUserId",
  async (user_id, thunkAPI) => {
    const {
      subscriptions: previousSubscriptions,
    } = thunkAPI.getState().subscription;
    try {
      const { subscriptions } = await send("POST", "api/subscriptions/user/", {
        user_id,
      });
      if (!isEqual(previousSubscriptions, subscriptions)) {
        thunkAPI.dispatch(updateSubscriptions({ subscriptions }));
        thunkAPI.dispatch(updateDeterminations(subscriptions));
      }
    } catch (e) {
      captureException(e, "Failed to fetch user subscriptions");
    }
  }
);

export const {
  updateSubscriptions,
  updateDeterminations,
} = subscriptionSlice.actions;

export default subscriptionSlice.reducer;
